/**
 * FlashBook recovery — the copy of the book this device keeps while it is being
 * written.
 *
 * A creator builds a book over an evening on a phone. The studio has no autosave
 * of its own, so until they press *Save as draft* the whole thing exists only in
 * the tab: a reload, a crash, a battery or an accidental back gesture takes all of
 * it. The audio is the exception and has been for a while — a take is uploaded the
 * moment it is recorded — which is exactly the asymmetry this module closes. The
 * readings were the safe half; the book is what was left.
 *
 * So the studio keeps a snapshot of the whole book as it is edited, and offers it
 * back the next time it opens. Three decisions shape how:
 *
 *  - **On this device, not on the server.** The snapshot is device storage, not a
 *    record. An autosave that quietly wrote `status: 'draft'` records would fill
 *    the creator's drafts list with books they never chose to save, and — worse —
 *    it would silently disarm the unsaved-work guard, which exists because
 *    *publishing* is a deliberate act. Keeping a copy is the app remembering for
 *    you; saving is the creator deciding. This only does the first.
 *  - **Replaced, not appended.** One slot, because there is one book in progress.
 *    A history of snapshots would need a browser to choose from, and the creator
 *    asked for their work back, not for a version control system.
 *  - **Offered, never applied.** Loading a snapshot over a book the studio already
 *    put on screen would be the app deciding which of two pieces of work is real.
 *    It is offered, with the time it was taken, and the creator says yes.
 *
 * The snapshot holds URIs, not bytes. Text, structure, the theme, the cover and
 * every reading come back exactly; a file the device no longer holds — a cover
 * photograph picked but never uploaded, because uploads happen at save — has to
 * be picked again. That limit is stated where the creator meets it rather than
 * discovered later.
 */
import {isLocalClipUrl} from '#/lib/flashbookClips'
import {device} from '#/storage'
import {type FlashBookCover, type FlashBookPage} from '#/types/flashbook'

/**
 * The book, in the shape a save would carry.
 *
 * Deliberately the *record's* shape rather than the studio's: a snapshot is not
 * the screen's state, and writing the screen's field names into storage would
 * make every rename in the studio a migration.
 */
export type FlashBookRecoveredBook = {
  title: string
  description: string
  themeId: string
  /**
   * The music, in the record's spelling rather than the screen's.
   *
   * A snapshot is not the screen's state (see the note above), and the two "no
   * music" states have to stay distinguishable here for the same reason they do
   * in the signature: a creator who chose silence and is handed the book back
   * must not find their theme's bed playing again.
   */
  backgroundMusicId?: string
  htmlThemeUrl?: string
  cover: FlashBookCover
  pages: FlashBookPage[]
}

export type FlashBookSnapshot = {
  /**
   * The draft record this work came from, or `null` for a book started here.
   *
   * This is what makes the offer trustworthy. Without it, a snapshot left behind
   * by one book would be offered over a different one — a creator opening an old
   * draft would be asked whether they want to restore the book they abandoned
   * last week, and the honest answer would be "no, and why are you asking".
   */
  draftUri: string | null
  /** When the snapshot was taken, so the offer can say how old it is. */
  savedAt: string
  /**
   * The book's own content fingerprint at the moment it was taken.
   *
   * Carried so the offer can answer the only question that matters when the
   * studio opens: *is this different from the book already on screen?* A
   * snapshot that matches what is loaded is not unfinished work, it is work that
   * has been dealt with.
   */
  signature: string
  book: FlashBookRecoveredBook
}

/**
 * The book a snapshot holds, as the studio hands it over.
 *
 * `signature` is passed in rather than recomputed, so there is exactly one
 * definition of "the content changed" in the codebase — the studio's
 * `flashBookContentSignature` — and the snapshot can never disagree with the
 * unsaved-work guard about whether the same two books are the same book.
 */
export function createFlashBookSnapshot({
  draftUri,
  signature,
  book,
  now = new Date().toISOString(),
}: {
  draftUri: string | null
  signature: string
  book: FlashBookRecoveredBook
  now?: string
}): FlashBookSnapshot {
  return {draftUri, signature, savedAt: now, book}
}

/**
 * Whether a book is worth keeping a copy of.
 *
 * The same question the unsaved-work guard asks, and deliberately the same
 * answer: a book with a title, a description, a cover or a single block. An empty
 * studio is not work, and offering to recover one would teach the creator to
 * dismiss the offer without reading it — which is how a recovery feature becomes
 * a dialog everybody clicks through.
 */
export function bookHasContent(book: {
  title: string
  description: string
  cover: FlashBookCover
  pages: readonly FlashBookPage[]
}): boolean {
  if (book.title.trim().length > 0) return true
  if (book.description.trim().length > 0) return true
  if (book.cover.imageUri || book.cover.audioUri || book.cover.videoUri)
    return true
  return book.pages.some(
    page => page.blocks.length > 0 || (page.title ?? '').trim().length > 0,
  )
}

/**
 * How many files the copy points at that only this device ever had.
 *
 * A reading is uploaded the moment it is recorded, so in practice this counts
 * the cover — its artwork, its video and its spoken introduction are uploaded at
 * save, and a save may never have happened. On the web those URIs are `blob:`s,
 * which die with the document; on a phone they are files in a cache.
 *
 * It is counted rather than assumed because the offer should only warn about what
 * is actually at stake: a snapshot of a book with no cover file comes back whole,
 * and telling the creator otherwise would teach them not to read the warning.
 */
export function unbackedMediaCount(snapshot: FlashBookSnapshot): number {
  const uris: (string | undefined)[] = [
    snapshot.book.cover.imageUri,
    snapshot.book.cover.audioUri,
    snapshot.book.cover.videoUri,
  ]
  for (const page of snapshot.book.pages) {
    for (const block of page.blocks) uris.push(block.readingAudioUrl)
  }
  return uris.filter(uri => isLocalClipUrl(uri) && !isCarriedBySnapshot(uri))
    .length
}

/**
 * Whether the copy carries the file itself.
 *
 * A `data:` URL *is* the bytes, written into the URL, so a snapshot that holds
 * one brings that image back whole — the file travels inside the copy and no
 * upload is ever needed. The local-scheme test the save pipeline uses is
 * deliberately wider (a `data:` URL still has to be uploaded before it can become
 * a record on the creator's PDS), so the narrower question is asked here rather
 * than loosening that one.
 *
 * This distinction is the difference between a warning and noise: a book whose
 * only local file is inline comes back complete, and warning about it is how the
 * warning stops being read on the day a `blob:` really does go missing.
 */
function isCarriedBySnapshot(url?: string | null): boolean {
  return !!url && url.startsWith('data:')
}

export function getFlashBookSnapshot(): FlashBookSnapshot | null {
  const snapshot = device.get(['flashbookRecovery'])
  return snapshot ?? null
}

export function putFlashBookSnapshot(snapshot: FlashBookSnapshot): void {
  device.set(['flashbookRecovery'], snapshot)
}

export function clearFlashBookSnapshot(): void {
  device.remove(['flashbookRecovery'])
}

/** Whether a snapshot belongs to the book the studio is currently holding. */
export function snapshotBelongsTo(
  snapshot: Pick<FlashBookSnapshot, 'draftUri'>,
  draftUri: string | null,
): boolean {
  return snapshot.draftUri === draftUri
}

/**
 * The snapshot worth offering back, or `null` when there is nothing to offer.
 *
 * Three ways a stored snapshot is not an offer, and each is a case a creator
 * would otherwise meet as a confusing dialog:
 *
 *  - it belongs to a *different* book;
 *  - it is exactly the book already on screen — nothing to recover;
 *  - it holds nothing (a book abandoned before a first word was typed).
 */
export function offerableSnapshot(
  snapshot: FlashBookSnapshot | null,
  current: {draftUri: string | null; signature: string},
): FlashBookSnapshot | null {
  if (!snapshot) return null
  if (!snapshotBelongsTo(snapshot, current.draftUri)) return null
  if (snapshot.signature === current.signature) return null
  if (!bookHasContent(snapshot.book)) return null
  return snapshot
}

/**
 * How long ago the copy was taken, for the offer to say.
 *
 * Lives in `#/lib/snapshotAge` because the Capsule studio asks the same question
 * about the same kind of copy; re-exported here so every existing reader of this
 * module keeps working.
 */
export {describeSnapshotAge, type SnapshotAge} from '#/lib/snapshotAge'
